import { useEffect, useState } from 'react';
import { useRouteError, useSearchParams } from 'react-router-dom';

import { Button, Card, LoadingSpinner, UserAlert } from '@hcs/design-system';
import { useExperienceFlag } from '@hcs/experience-flags';
import { createCustomErrorWithOriginal, logException } from '@hcs/utils';

import styles from './RouterErrorBoundary.module.css';

const dataHcName = 'router-error-boundary';

const RELOAD_ATTEMPT_PARAM = 'reloadAttempt';

const RouterErrorBoundaryLoadingError = createCustomErrorWithOriginal(
  'RouterErrorBoundaryLoadingError',
);
const RouterErrorBoundaryUnhandledError = createCustomErrorWithOriginal(
  'RouterErrorBoundaryUnhandledError',
);

const refreshWindow = () => window.location.reload();

interface ErrorCardProps {
  cardTitle: string;
  errorText: string;
}

const ErrorCard = ({ cardTitle, errorText }: ErrorCardProps) => {
  return (
    <div className={styles.Container}>
      <Card
        dataHcName={`${dataHcName}-loading-error-card`}
        title={cardTitle}
        className={styles.ErrorCard}
      >
        <UserAlert
          type="error"
          dataHcName={`${dataHcName}-loading-error-user-alert`}
          text={errorText}
        />
        <Button
          dataHcName={`${dataHcName}-refresh-button`}
          onClick={refreshWindow}
        >
          Refresh
        </Button>
      </Card>
    </div>
  );
};

const isLoadingError = (error: unknown): boolean => {
  return (
    error instanceof Error &&
    (error?.message.includes('Failed to fetch dynamically imported module') ||
      error?.message.includes('Unable to preload CSS for') ||
      error?.message.includes('Importing a module script failed'))
  );
};

export const RouterErrorBoundary = () => {
  const error = useRouteError();
  const [searchParams, setSearchParams] = useSearchParams();
  const [shouldAttemptReload] = useState<boolean>(
    searchParams.get(RELOAD_ATTEMPT_PARAM) === null,
  );
  const isUsingPexpRedesign = useExperienceFlag('PEXP_REDESIGN');

  useEffect(() => {
    if (shouldAttemptReload) {
      // attempt reload
      setSearchParams((searchParams) => {
        searchParams.set(RELOAD_ATTEMPT_PARAM, 'true');
        return searchParams;
      });
      refreshWindow();
    } else if (isLoadingError(error)) {
      logException(
        new RouterErrorBoundaryLoadingError(
          'RouterErrorBoundary - loading error',
          error,
        ),
        {
          pexpOneFive: !isUsingPexpRedesign,
        },
        ['router-error-boundary-loading-error'],
      );
    } else {
      logException(
        new RouterErrorBoundaryUnhandledError(
          'RouterErrorBoundary - unhandled error',
          error,
        ),
        {
          pexpOneFive: !isUsingPexpRedesign,
        },
      );
    }
  }, [error, shouldAttemptReload, setSearchParams, isUsingPexpRedesign]);

  if (shouldAttemptReload) {
    // we know we'll attempt a reload for this error, so show loader until we reload
    return <LoadingSpinner dataHcName={`${dataHcName}-loading-spinner`} />;
  } else if (isLoadingError(error)) {
    return (
      <ErrorCard
        cardTitle="Error loading the app"
        errorText="An error occurred loading the app. Please check your internet connection and refresh"
      />
    );
  } else {
    return (
      <ErrorCard
        cardTitle="An unhandled error occurred"
        errorText="The error has been reported. Please try refreshing."
      />
    );
  }
};
